import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { NAVY_FORM } from './colors';
import { LoadError } from './ui';
import { useDarkMode } from './DarkModeContext';
// DATEDAY: date-only values shown as their calendar day in every timezone.
import { toDisplayDate } from './dateOnly';

const API = '/api';

// ATTESTVIEW: one employee's attestation, opened by clicking their name in the Attestations view.
//
// RENDERS INSIDE THE COMPLIANCE SHELL, like the compliance employee card: this component owns no
// page chrome and no way back. The shell supplies the topbar, the sidebar and the breadcrumb, and
// the breadcrumb owns navigating back - so a refresh, a direct URL and the browser Back button all
// behave the same way they do on the employee card.
//
// IT IS READ ONLY, and deliberately so. An attestation has exactly two states (pending /
// completed) and ATTESTSELF made completion SELF-ONLY by confirmed decision: the privileged
// carve-out that once let compliance tick someone else's box was removed. So there is no approval
// or completion control to offer here, and inventing one would both reverse that decision and
// create a second approval mechanism beside the declarations' own. The server says as much in the
// payload (`approval_available`), and the note below renders the server's own sentence rather than
// a client-side guess, so the screen and the rule cannot drift apart.
//
// SCOPE IS THE SERVER'S JOB. This screen is reachable by direct URL, so the detail endpoint
// re-checks the entity scope on every call (with the dual-entity attestation carve-out) and
// answers 403 for an employee outside the officer's entity. A refusal renders as the error state
// below rather than as an empty screen.

const TYPE_LABELS = {
  pad: 'Personal Account Dealing',
  geh: 'Gifts and Entertainment',
  outside_affiliations: 'Outside Affiliations',
  public_officials: 'Public Officials',
};

const typeLabel = (t) => TYPE_LABELS[t] || (t || '-');

const fmtDateTime = (iso) => {
  if (!iso) return '-';
  const d = toDisplayDate(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric',
                                     hour: '2-digit', minute: '2-digit' });
};

export default function ComplianceAttestationDetail({ attestationId }) {
  const { isDarkMode } = useDarkMode();
  // DECLRENAME: this view had no translator of its own, which is why its policy text was the raw
  // stored English string. Same hook and same key family the Attestations tab already uses.
  const { t } = useTranslation();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    if (!attestationId) return;
    setLoading(true);
    setError(null);
    axios.get(`${API}/compliance/attestations/${attestationId}/detail`)
      .then(res => { setDetail(res.data || null); setLoading(false); })
      .catch(e => {
        const d = e.response?.data?.detail;
        setError(typeof d === 'string' ? d : 'Could not load this attestation.');
        setDetail(null);
        setLoading(false);
      });
  }, [attestationId]);

  useEffect(() => { load(); }, [load]);

  const T = {
    card:  { backgroundColor: isDarkMode ? '#16233d' : '#fff', borderRadius: '10px', padding: '20px',
             marginBottom: '16px',
             border: `0.5px solid ${isDarkMode ? 'rgba(201,168,76,0.15)' : '#EEE'}`,
             boxShadow: isDarkMode ? 'none' : '0 1px 3px rgba(0,0,0,0.05)' },
    title: { fontSize: '14px', fontWeight: 600, color: isDarkMode ? '#e8eaf0' : NAVY_FORM, margin: '0 0 14px 0' },
    label: { fontSize: '11px', color: isDarkMode ? '#8b95a8' : '#aaa', textTransform: 'uppercase',
             letterSpacing: '0.05em', fontWeight: 600, margin: '0 0 4px 0' },
    value: { fontSize: '14px', color: isDarkMode ? '#e8eaf0' : '#333', margin: 0 },
    muted: { color: isDarkMode ? '#8b95a8' : '#667085', fontStyle: 'italic', fontSize: '13px', margin: 0 },
    pill:  (bg, fg) => ({ backgroundColor: bg, color: fg, borderRadius: '10px', padding: '2px 8px',
             fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap' }),
  };

  if (loading) return <div style={T.card}><p style={T.muted}>Loading...</p></div>;
  if (error) {
    return (
      <div style={T.card}>
        <LoadError message={error} onRetry={load} />
      </div>
    );
  }
  if (!detail) return <div style={T.card}><p style={T.muted}>Attestation not found.</p></div>;

  const emp = detail.employee || {};
  const done = detail.status === 'completed';

  return (
    <div>
      {/* HEADER - name, job title and entity, the same header shape the employee card uses. */}
      <div style={T.card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ width: 40, height: 40, borderRadius: '50%', backgroundColor: NAVY_FORM,
                        color: '#fff', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', fontSize: 16, fontWeight: 600, flexShrink: 0 }}>
            {(emp.name || '?').charAt(0)}
          </div>
          <div style={{ minWidth: 0 }}>
            <p style={{ ...T.value, fontWeight: 600, fontSize: '18px' }}>{emp.name || '-'}</p>
            <p style={{ ...T.muted, fontStyle: 'normal' }}>
              {emp.job_title || '-'}{emp.entity ? ` · ${emp.entity}` : ''}
            </p>
          </div>
          <span style={{ marginLeft: 'auto' }}>
            {done
              ? <span style={T.pill('#eaf3ee', '#2D4A3E')}>Submitted</span>
              : <span style={T.pill('#fdf8ee', '#7a4d00')}>Pending</span>}
          </span>
        </div>
      </div>

      {/* THE ATTESTATION ITSELF */}
      <div style={T.card}>
        <h3 style={T.title}>Attestation</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                      gap: '14px' }}>
          <div>
            <p style={T.label}>Type</p>
            <p style={T.value}>{typeLabel(detail.attestation_type)}</p>
          </div>
          <div>
            <p style={T.label}>Period</p>
            <p style={T.value}>{detail.period || '-'}</p>
          </div>
          <div>
            <p style={T.label}>Status</p>
            <p style={T.value}>{done ? 'Submitted' : 'Pending'}</p>
          </div>
          <div>
            <p style={T.label}>Submitted at</p>
            <p style={T.value}>{fmtDateTime(detail.attested_at)}</p>
          </div>
          <div>
            <p style={T.label}>Confirmation ticked</p>
            <p style={T.value}>{detail.checkbox_confirmed ? 'Yes' : 'No'}</p>
          </div>
        </div>

        {/* DECLRENAME: the POLICY TEXT, resolved exactly as the Attestations tab on the Declarations
            screen resolves it - the translated wording for this attestation type, falling back to
            the server's stored attestation_text when no translation exists for the type.
            ADDED HERE BECAUSE THIS VIEW IS NOW THE ONLY ONE compliance staff have: the duplicate tab
            is gone from the Declarations screen, so this detail had to carry everything that tab
            showed before it could replace it. It previously rendered the raw stored English string
            only, which meant a French-reading officer saw English policy wording on this screen and
            French on the other. Same single key family, so the two can no longer disagree. */}
        {(t(`compliance.attestations.wording.${detail.attestation_type}`,
            { defaultValue: detail.attestation_text || '' })) && (
          <div style={{ marginTop: '16px', paddingTop: '14px',
                        borderTop: `1px solid ${isDarkMode ? 'rgba(201,168,76,0.15)' : '#eee'}` }}>
            <p style={T.label}>Attestation text</p>
            <p style={{ ...T.value, lineHeight: 1.6 }}>
              {t(`compliance.attestations.wording.${detail.attestation_type}`,
                 { defaultValue: detail.attestation_text || '' })}
            </p>
          </div>
        )}
      </div>

      {/* WHY THERE IS NO ACTION HERE. The server's own sentence, not a client-side guess, so the
          screen cannot state a rule the backend does not hold. */}
      {detail.approval_available === false && detail.approval_note && (
        <div style={T.card}>
          <h3 style={T.title}>Compliance action</h3>
          <p style={T.muted}>{detail.approval_note}</p>
        </div>
      )}
    </div>
  );
}
